/**
 * /app/vendor/customers/:orgId — one customer foundry from the vendor's
 * perspective: the vendor's equipment slice at that foundry, with the same
 * equipment-scoped drill-in. Reuses the cached fleet queries (filtered to this
 * org client-side); it never shows the foundry's own process/twin or another
 * vendor's gear. Gated on useOrg().isVendorAdmin. See tenancy-vendor-portal.md §4.
 */

import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Ban, Check, Copy, Loader2, Pencil, Power, Send, X } from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Badge } from "@/lib/ui/badge";
import { Card } from "@/lib/ui/card";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";
import { isLiveApi } from "@/lib/api";
import { vendor, type VendorCustomerList } from "@/lib/api/vendor";
import { EquipmentTable } from "@/routes/vendor/dashboard";

export function VendorCustomerDetail() {
  const { isVendorAdmin } = useOrg();
  const { orgId } = useParams<{ orgId: string }>();
  const enabled = isLiveApi && isVendorAdmin;
  const customersQuery = useQuery({
    queryKey: ["vendor", "customers"],
    queryFn: () => vendor.listCustomers(),
    enabled,
    staleTime: 30_000,
  });
  const equipmentQuery = useQuery({
    queryKey: ["vendor", "customer-equipment", orgId],
    queryFn: () => vendor.customerEquipment(orgId!),
    enabled: enabled && !!orgId,
    staleTime: 30_000,
  });

  if (!isVendorAdmin) return <Navigate to="/app" replace />;

  const customer = customersQuery.data?.items.find((c) => c.id === orgId);
  // Already scoped to this customer by the endpoint — no client-side filter (the
  // flat fleet list is page-capped and dropped customers past the first 50).
  const equipment = equipmentQuery.data?.items ?? [];
  const online = equipment.filter((e) => e.online).length;
  const gateways = equipment.filter((e) => e.kind === "gateway").length;

  return (
    <PageContainer className="max-w-6xl">
      <PageHeader
        breadcrumb={
          <>
            <Link to="/app" className="hover:text-on-surface">
              App
            </Link>
            <span aria-hidden>/</span>
            <Link to="/app/vendor" className="hover:text-on-surface">
              Fleet
            </Link>
            <span aria-hidden>/</span>
            <span className="text-on-surface">{customer?.name ?? "Customer"}</span>
          </>
        }
        title={customer?.name ?? "Customer"}
        description="The equipment you manufacture at this foundry — live status and readings. You don't see the foundry's own process or another vendor's gear."
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Equipment" value={`${equipment.length}`} />
        <Stat label="Online" value={`${online} / ${equipment.length}`} />
        <Stat label="Gateways" value={`${gateways}`} />
        <Stat label="Instruments" value={`${equipment.length - gateways}`} />
      </div>

      {customer && (
        <div className="mt-6 space-y-4">
          <ContactEmail
            key={customer.id}
            orgId={customer.id}
            initialEmail={customer.contact_email}
          />
          <InviteAdmin
            key={`invite-${customer.id}`}
            orgId={customer.id}
            initialEmail={customer.contact_email}
          />
          <PlantStatus
            key={`status-${customer.id}`}
            orgId={customer.id}
            initialDisabled={customer.is_disabled}
          />
        </div>
      )}

      <div className="mt-6">
        {equipmentQuery.isLoading ? (
          <p className="text-sm text-on-surface-muted">Loading equipment…</p>
        ) : equipment.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-elevated/40 p-10 text-center text-sm text-on-surface-muted">
            No equipment of yours at this customer yet.
          </div>
        ) : (
          <EquipmentTable items={equipment} />
        )}
      </div>
    </PageContainer>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-4">
      <div className="text-[11px] uppercase tracking-wide text-on-surface-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-on-surface">{value}</div>
    </Card>
  );
}

/**
 * Editable contact email for the customer foundry — the real address the vendor
 * invites them at. Reads the seed value from the cached customers list, PATCHes
 * on save, then updates local state (+ the cached list, so the fleet table and a
 * back-nav agree). Empty clears it; a non-empty value must contain "@".
 */
function ContactEmail({ orgId, initialEmail }: { orgId: string; initialEmail: string | null }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState<string | null>(initialEmail);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialEmail ?? "");

  const mutation = useMutation({
    mutationFn: (value: string) => vendor.patchCustomer(orgId, value),
    onSuccess: (res) => {
      setEmail(res.contact_email);
      setEditing(false);
      // Keep the cached customers list in sync so the fleet table + back-nav
      // reflect the new value without a refetch.
      queryClient.setQueryData<VendorCustomerList>(["vendor", "customers"], (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((c) =>
                c.id === orgId ? { ...c, contact_email: res.contact_email } : c,
              ),
            }
          : prev,
      );
      toast.push("ok", res.contact_email ? "Contact email updated." : "Contact email cleared.");
    },
    onError: () => toast.push("error", "Couldn't update the contact email."),
  });

  const trimmed = draft.trim();
  const valid = trimmed === "" || trimmed.includes("@");

  const startEdit = () => {
    setDraft(email ?? "");
    setEditing(true);
  };
  const cancel = () => {
    setDraft(email ?? "");
    setEditing(false);
  };
  const save = () => {
    if (!valid || mutation.isPending) return;
    mutation.mutate(trimmed);
  };

  return (
    <Card className="p-4">
      <div className="text-[11px] uppercase tracking-wide text-on-surface-muted">Contact email</div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              autoFocus
              type="email"
              placeholder="owner@foundry.com"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") cancel();
              }}
              className="sm:max-w-sm"
            />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={save} disabled={!valid || mutation.isPending}>
                {mutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 size-4 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Check className="mr-1.5 size-4" /> Save
                  </>
                )}
              </Button>
              <Button size="sm" variant="ghost" onClick={cancel} disabled={mutation.isPending}>
                <X className="mr-1.5 size-4" /> Cancel
              </Button>
            </div>
          </div>
          {!valid && (
            <p className="text-[11px] text-danger">Enter a valid email, or leave it empty to clear.</p>
          )}
          <p className="text-[11px] text-on-surface-muted">
            Set the customer's real email before inviting them.
          </p>
        </div>
      ) : (
        <div className="mt-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            {email ? (
              <span className="truncate text-sm text-on-surface">{email}</span>
            ) : (
              <span className="text-sm italic text-on-surface-muted">Not set</span>
            )}
            <p className="mt-0.5 text-[11px] text-on-surface-muted">
              Set the customer's real email before inviting them.
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={startEdit} className="shrink-0">
            <Pencil className="mr-1.5 size-4" /> Edit
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * Invite the customer foundry's admin. Mints a pending admin invite on the
 * child org (POST /v1/vendor/customers/{org_id}/invites) and surfaces the
 * returned accept link, copyable — the vendor sends it to the customer, who
 * signs up and lands in their own org. Mirrors ContactEmail's mutation + toast
 * pattern; prefills from the contact email so the common path is one click.
 */
function InviteAdmin({ orgId, initialEmail }: { orgId: string; initialEmail: string | null }) {
  const toast = useToast();
  const [email, setEmail] = useState(initialEmail ?? "");
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: (value: string) => vendor.inviteCustomerAdmin(orgId, value),
    onSuccess: (res) => {
      setLink(res.link);
      setCopied(false);
      toast.push("ok", "Invite created — send the link to the customer's admin.");
    },
    onError: () => toast.push("error", "Couldn't create the invite."),
  });

  const trimmed = email.trim();
  const valid = trimmed.includes("@");

  const invite = () => {
    if (!valid || mutation.isPending) return;
    mutation.mutate(trimmed);
  };
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is visible to copy manually */
    }
  };

  return (
    <Card className="p-4">
      <div className="text-[11px] uppercase tracking-wide text-on-surface-muted">Invite admin</div>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          type="email"
          placeholder="owner@foundry.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setLink(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") invite();
          }}
          className="sm:max-w-sm"
        />
        <Button
          size="sm"
          onClick={invite}
          disabled={!valid || mutation.isPending}
          className="shrink-0"
        >
          {mutation.isPending ? (
            <>
              <Loader2 className="mr-1.5 size-4 animate-spin" /> Inviting…
            </>
          ) : (
            <>
              <Send className="mr-1.5 size-4" /> Invite
            </>
          )}
        </Button>
      </div>
      {!valid && trimmed !== "" && (
        <p className="mt-2 text-[11px] text-danger">Enter a valid email.</p>
      )}
      {link ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <Input readOnly value={link} className="font-mono text-xs" />
            <Button size="sm" variant="secondary" onClick={copy} className="shrink-0">
              {copied ? (
                <>
                  <Check className="mr-1.5 size-4" /> Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1.5 size-4" /> Copy
                </>
              )}
            </Button>
          </div>
          <p className="text-[11px] text-on-surface-muted">
            Send this link to the customer's admin — they sign up and land in their org.
          </p>
        </div>
      ) : (
        <p className="mt-2 text-[11px] text-on-surface-muted">
          Send an admin invite link to this foundry's owner.
        </p>
      )}
    </Card>
  );
}

/**
 * Enable/disable control for the customer foundry. Disabling flips the org's
 * is_disabled gate (orgctx.Middleware then 403s org_disabled on every request),
 * so the plant's users can no longer sign in or reach their data until it is
 * re-enabled — a two-step confirm guards that destructive-ish action, while
 * re-enabling is a single click. Mirrors ContactEmail: POST, then sync local
 * state + the cached customers list (so the fleet Active/Disabled badge + a
 * back-nav agree without a refetch) + a toast.
 */
function PlantStatus({ orgId, initialDisabled }: { orgId: string; initialDisabled: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [disabled, setDisabled] = useState(initialDisabled);
  const [confirming, setConfirming] = useState(false);

  const mutation = useMutation({
    mutationFn: (next: boolean) => vendor.setCustomerDisabled(orgId, next),
    onSuccess: (res) => {
      setDisabled(res.is_disabled);
      setConfirming(false);
      // Keep the cached customers list in sync so the fleet Active/Disabled
      // badge + a back-nav reflect the new state without a refetch.
      queryClient.setQueryData<VendorCustomerList>(["vendor", "customers"], (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((c) =>
                c.id === orgId ? { ...c, is_disabled: res.is_disabled } : c,
              ),
            }
          : prev,
      );
      toast.push("ok", res.is_disabled ? "Plant disabled." : "Plant re-enabled.");
    },
    onError: () => toast.push("error", "Couldn't update the plant's status."),
  });

  return (
    <Card className="p-4">
      <div className="text-[11px] uppercase tracking-wide text-on-surface-muted">Plant status</div>
      <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          {disabled ? (
            <Badge variant="danger">Disabled</Badge>
          ) : (
            <Badge variant="success">Active</Badge>
          )}
          <p className="mt-1.5 text-[11px] text-on-surface-muted">
            {disabled
              ? "This plant's users can no longer sign in or access their data until re-enabled."
              : "This plant is active — its users can sign in and access their data."}
          </p>
        </div>
        {disabled ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => !mutation.isPending && mutation.mutate(false)}
            disabled={mutation.isPending}
            className="shrink-0"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-1.5 size-4 animate-spin" /> Enabling…
              </>
            ) : (
              <>
                <Power className="mr-1.5 size-4" /> Enable plant
              </>
            )}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="danger"
            onClick={() => setConfirming(true)}
            disabled={mutation.isPending || confirming}
            className="shrink-0"
          >
            <Ban className="mr-1.5 size-4" /> Disable plant
          </Button>
        )}
      </div>
      {confirming && !disabled && (
        <div className="mt-3 rounded-lg border border-danger/30 bg-danger/5 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
            <div className="min-w-0">
              <p className="text-sm text-on-surface">
                Disable this plant? This plant's users can no longer sign in or access their data
                until re-enabled.
              </p>
              <div className="mt-2.5 flex items-center gap-2">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => !mutation.isPending && mutation.mutate(true)}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="mr-1.5 size-4 animate-spin" /> Disabling…
                    </>
                  ) : (
                    <>
                      <Ban className="mr-1.5 size-4" /> Confirm disable
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirming(false)}
                  disabled={mutation.isPending}
                >
                  <X className="mr-1.5 size-4" /> Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
